import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Fab
} from "@mui/material";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Block as BlockIcon
} from "@mui/icons-material";

import LocalizacionForm from "./LocalizationForm";
import { useLocalizaciones } from "../../../hooks/useLocalizations";
import { useAuthContext } from "../../../context/AuthProvider";

const LocalizationsDashboard = () => {
  const [open, setOpen] = useState(false);
  const [editLoc, setEditLoc] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { user } = useAuthContext();
  const { localizaciones, isLoading, createMutation, updateMutation, deleteMutation } = useLocalizaciones();

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
  };

  const handleSubmitLocalizacion = (data) => {
    if (!user) {
      showAlert("Debe iniciar sesión", "error");
      return;
    }

    if (editLoc) {
      updateMutation.mutate(
        {
          id: editLoc.locid,
          data: {
            ...data,
            estreg: editLoc.estreg || "A",
            updbyid: user.EmpID
          }
        },
        {
          onSuccess: () => {
            showAlert("Localización actualizada con éxito");
            setOpen(false);
            setEditLoc(null);
          },
          onError: (error) => showAlert(error.message || "Error al actualizar", "error")
        }
      );
    } else {
      createMutation.mutate(
        {
          ...data,
          loctip: "RUTA",
          crebyid: user.EmpID,
        },
        {
          onSuccess: () => {
            showAlert("Localización creada con éxito");
            setOpen(false);
          },
          onError: (error) => showAlert(error.message || "Error al crear", "error")
        }
      );
    }
  };

  const handleDeactivateLoc = (id) => {
    deleteMutation.mutate(id, {
      onSuccess: () => showAlert("Localización desactivada con éxito"),
      onError: (error) => showAlert(error.message || "Error al desactivar", "error")
    });
  };
  
  const filtered = localizaciones?.filter((loc) => {
    const isRouteLocation = (loc.loctip || "RUTA") === "RUTA";

    const matchesSearch = loc.locnom
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());

    return isRouteLocation && matchesSearch;
  });

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>Localizaciones</Typography>
      </Box>

      <Box display="flex" gap={2} mb={2}>
        <TextField
          label="Buscar por nombre..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead sx={{ backgroundColor: "#e0e0e0" }}>
            <TableRow>
              <TableCell><strong>Nombre</strong></TableCell>
              <TableCell><strong>Descripción</strong></TableCell>
              <TableCell><strong>Latitud</strong></TableCell>
              <TableCell><strong>Longitud</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell align="center"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((loc) => (
              <TableRow key={loc.locid} hover>
                <TableCell>{loc.locnom}</TableCell>
                <TableCell>{loc.locdes || "—"}</TableCell>
                <TableCell>{Number(loc.loclat).toFixed(6)}</TableCell>
                <TableCell>{Number(loc.loclng).toFixed(6)}</TableCell>
                <TableCell>
                  <Chip
                    label={loc.estreg === "A" ? "ACTIVO" : "INACTIVO"}
                    color={loc.estreg === "A" ? "success" : "warning"}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => setEditLoc(loc) || setOpen(true)}>
                      <EditIcon color="primary" />
                    </IconButton>
                  </Tooltip>
                  {loc.estreg !== "E" && (
                    <Tooltip title="Desactivar">
                      <IconButton size="small" onClick={() => handleDeactivateLoc(loc.locid)}>
                        <BlockIcon color="error" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filtered?.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: "fixed", bottom: 20, right: 20 }}
        onClick={() => {
          setEditLoc(null);
          setOpen(true);
        }}
      >
        <AddIcon />
      </Fab>

      {open && (
        <LocalizacionForm
          open={open}
          handleClose={() => setOpen(false) || setEditLoc(null)}
          onSubmit={handleSubmitLocalizacion}
          defaultValues={editLoc || {}}
        />
      )}

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert severity={alert.severity}>{alert.message}</Alert>
      </Snackbar>
    </Paper>
  );
};

export default LocalizationsDashboard;
